import React, { useContext, useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { Container, Form, Button } from 'react-bootstrap';
import { FaTv } from 'react-icons/fa';
import { ProductsContext } from '../contexts/ProductsContext';
import ProductCard from '../components/ProductCard';
import Masonry from 'react-masonry-css';
import CrtSearch from '../components/CrtSearch';
import CRTSubmissionModal from '../components/CRTSubmissionModal';
import './Home.css';

const API_URL = process.env.REACT_APP_API_URL;

const Home = () => {
  const { loadMoreProducts, resetProducts } = useContext(ProductsContext);
  const [showcaseProducts, setShowcaseProducts] = useState([]);
  const [loadingShowcase, setLoadingShowcase] = useState(false);
  const [randomRgbCrtList, setRandomRgbCrtList] = useState([]);
  const [interestingCrts, setInterestingCrts] = useState([]);
  const [showCrtModal, setShowCrtModal] = useState(false);

  // Featured YouTube videos data
  const featuredVideos = [
    {
      id: '1',
      title: 'How to RGB Mod Your CRT TV',
      thumbnail: 'https://i.ytimg.com/vi/t1kJSg2I5t8/hqdefault.jpg',
      url: 'https://www.youtube.com/watch?v=t1kJSg2I5t8&t=1062s',
      author: 'Retro Tech',
      icon: 'https://yt3.ggpht.com/ytc/AIdro_mYzDahzN7HgnFy-8gArA5eeeWcAOJnTt5Gwg0fGLMUvMo=s88-c-k-c0x00ffffff-no-rj'
    },
    {
      id: '2',
      title: 'Let\'s RGB Mod My CRT, the Toshiba 20AF45!',
      thumbnail: 'https://img.youtube.com/vi/fYhR56LCfyM/hqdefault.jpg',
      url: 'https://www.youtube.com/watch?v=fYhR56LCfyM&t=520s',
      author: 'YourCoolUncleMarisa',
      icon: 'https://yt3.ggpht.com/TdMEnVmbAHwaa-0Xp51ylhJrZUzPTsjuKQJNp3q8Q7idcQmiTjkUSY_v65O3_yaNGeGnJ-D63Mk=s88-c-k-c0x00ffffff-no-rj'
    },
    {
      id: '3',
      title: 'The A to Z of RGB Modding a CRT TV... YOU CAN DO IT!!!',
      thumbnail: 'https://img.youtube.com/vi/r5SXXBANvhk/hqdefault.jpg',
      url: 'https://www.youtube.com/watch?v=r5SXXBANvhk&t=1618s',
      author: 'MarcoRetro',
      icon: 'https://yt3.ggpht.com/VrCyhVKqgA2_6FBTGSQyrUJbV9hlnmM8YXr_8S8m0ySM7sijvf_RBN0tOnTMobOMplBjfpJ74Q=s88-c-k-c0x00ffffff-no-rj'
    },
    {
      id: '4',
      title: 'Adding RGB scart to my dream CRT (AA-1 Chassis RGB mod)',
      thumbnail: 'https://img.youtube.com/vi/9r-x-I-lBC0/hqdefault.jpg',
      url: 'https://www.youtube.com/watch?v=9r-x-I-lBC0',
      author: 'Red Cow Arcade',
      icon: 'https://yt3.googleusercontent.com/U-u7Jn8v52G4dH8QvJ3b5-8XcXHwi2mmPgAhfdVaJvaAQRIq0kvPk-S4IUaXW7idIHE0oxcaFQ=s160-c-k-c0x00ffffff-no-rj'
    },
    {
      id: '5',
      title: 'Why RGB is better than component - Toshiba AF CRT Mux mod',
      thumbnail: 'https://img.youtube.com/vi/BpTCZDgWfgE/hqdefault.jpg',
      url: 'https://www.youtube.com/watch?v=BpTCZDgWfgE',
      author: 'Retro Tech',
      icon: 'https://yt3.ggpht.com/ytc/AIdro_mYzDahzN7HgnFy-8gArA5eeeWcAOJnTt5Gwg0fGLMUvMo=s88-c-k-c0x00ffffff-no-rj'
    },
    {
      id: '6',
      title: 'Gamers\' Basics of Modding a CRT TV and the Sunthar RGB Mux Kit',
      thumbnail: 'https://img.youtube.com/vi/iDHtHPQztrs/hqdefault.jpg',
      url: 'https://www.youtube.com/watch?v=iDHtHPQztrs&t=300s',
      author: 'Mr. Brown\'s Basement',
      icon: 'https://yt3.ggpht.com/HXM_BwN3UL2W6_Tw7CRkfqUcpCv_236AQTZFsIpm_Nb9R0DiA_iJFcnOJ0dFXdJvt-dxElambcE=s88-c-k-c0x00ffffff-no-rj'
    }
  ];

  useEffect(() => {
    resetProducts(); // Clear existing products before loading
    fetchShowcaseProducts();
    fetchRandomRgbCrtList();
    fetchInterestingCrts();
  }, []);

  const fetchShowcaseProducts = async () => {
    setLoadingShowcase(true);
    try {
      const showcase = await loadMoreProducts(['showcase'], 24, 1); // Fetch 18 showcase products from page 1
      setShowcaseProducts(showcase || []);
    } catch (err) {
      console.error('Error loading showcase products:', err);
    } finally {
      setLoadingShowcase(false);
    }
  };

  const fetchRandomRgbCrtList = async () => {
    try {
      const response = await fetch(`${API_URL}/crts/moddable?types=rgb&moddable=yes&hasImage=true&limit=8`);
      const allCrts = await response.json();
      // Only include CRTs with a non-empty gallery_images array and a valid URL in the first image
      const crtsWithImages = allCrts.filter(crt =>
        Array.isArray(crt.gallery_images) && crt.gallery_images.length > 0 && (crt.gallery_images[0].compressed_url || crt.gallery_images[0].url)
      );
      setRandomRgbCrtList(crtsWithImages);
    } catch (err) {
      console.error('Error loading random RGB CRTs:', err);
    }
  };

  const fetchInterestingCrts = async () => {
    try {
      const response = await fetch(`${API_URL}/crts/interesting?hasImage=true&limit=4`);
      const data = await response.json();
      setInterestingCrts(data);
    } catch (err) {
      console.error('Error loading interesting CRTs:', err);
    }
  };

  // Masonry breakpoints for responsiveness
  const masonryBreakpoints = {
    default: 4, // Number of columns for default (large screens)
    1400: 4,    // Number of columns for tablets
    768: 3,     // Number of columns for smaller tablets
    480: 1      // Number of columns for mobile
  };

  return (
    <>
      <Container>
        <ToastContainer />
        <div className="home-container">
          <div className="d-flex justify-content-end align-items-center mb-4">
            <Button 
              variant="primary" 
              onClick={() => setShowCrtModal(true)}
              style={{
                backgroundColor: '#fca311',
                color: '#000',
                border: 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <FaTv style={{ verticalAlign: 'middle', marginRight: '8px', marginBottom: '2px' }} /> Submit Your CRT
            </Button>
          </div>
          <p>
            <img className="home-logo-gif" src="./logo.gif" alt="Sunthar Logo" />
          </p>
          <p>Empowering retro tech enthusiasts to restore, reuse, and share their passion — keeping classic hardware alive and out of landfills.</p>
        </div>

        {/* CRT Submission Modal */}
        <CRTSubmissionModal 
          show={showCrtModal}
          onHide={() => setShowCrtModal(false)}
          submissionType="new"
        />

        {/* CRT Model Search */}
        <div className="crt-search-container">
          <CrtSearch placeholder="Search for your CRT model..." />
        </div>

        {/* Featured YouTube Videos Section */}
        <div className="youtube-videos-section">
          <h2>Featured Videos</h2>
          <div className="youtube-videos-container">
            {featuredVideos.map((video) => (
              <a 
                key={video.id} 
                href={video.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="youtube-video-card"
              >
                <div className="youtube-thumbnail-wrapper">
                  <img 
                    src={video.thumbnail} 
                    alt={video.title} 
                    className="youtube-thumbnail"
                  />
                  <img 
                    src={video.icon} 
                    alt={`${video.author} channel icon`} 
                    className="youtube-author-icon"
                  />
                </div>
                <div className="youtube-video-info">
                  <h3 className="youtube-video-title">{video.title}</h3>
                  <div className="youtube-author-info">
                    <p className="youtube-author-name">{video.author}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Interesting CRTs Section */}
        <div style={{ padding: '15px 0'}}>
          <h2>Interesting CRT TVs</h2>
        </div>
        <div className="row">
          {interestingCrts.map((crt) => (
            <div className="col-xs-12 col-sm-4 col-md-3 mb-3" key={crt._id}>
              <a href={crt.slug ? `/crts/${crt.slug}` : '#'} style={{ textDecoration: 'none', color: 'inherit' }}>
                <ProductCard
                  product={{
                    name: crt.title,
                    images: crt.gallery_images,
                    slug: crt.slug,
                    store_logo: 'none',
                    author_name: crt.authors && crt.authors.length > 0 && crt.authors[0].author_name ? crt.authors[0].author_name : null
                  }}
                  showDescription={false}
                />
              </a>
            </div>
          ))}
        </div>

        {/* Random RGB Moddable CRTs Section */}
        <div style={{ padding: '15px 0'}}>
          <h2>RGB Moddable CRTs</h2>
        </div>
        <div className="row">
          {randomRgbCrtList.map((crt) => (
            <div className="col-xs-12 col-sm-4 col-md-3 mb-3" key={crt._id}>
              <a href={crt.slug ? `/crts/${crt.slug}` : '#'} style={{ textDecoration: 'none', color: 'inherit' }}>
                <ProductCard
                  product={{
                    name: crt.title,
                    images: crt.gallery_images,
                    slug: crt.slug,
                    store_logo: 'none'
                  }}
                  showDescription={false}
                />
              </a>
            </div>
          ))}
        </div>

        {/* Button to Show More Articles */}
        <div className="see-more-container mt-4 mb-4 text-center">
          <a href="/moddable" className="btn btn-primary">
            Show more moddable CRTs
          </a>
        </div>

        {/* Showcase Section */}
        <div style={{ padding: '15px 0'}}>
          <h2>Latest Additions</h2>
        </div>
        {loadingShowcase ? (
          <p>Loading latest additions...</p>
        ) : (
          <Masonry
            breakpointCols={masonryBreakpoints}
            className="masonry-grid"
            columnClassName="masonry-grid-column"
          >
            {showcaseProducts.map((product) => (
              <div key={product._id}>
                <ProductCard product={product} showDescription={false} />
              </div>
            ))}
          </Masonry>
        )}

        {/* Button to Show More Articles */}
        <div className="see-more-container mt-4 mb-4 text-center">
          <a href="/articles" className="btn btn-primary">
            Show more articles
          </a>
        </div>
      </Container>
    </>
  );
};

export default Home;



